import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchSongs } from "../api/songs";
import MusicPlayer from "../components/MusicPlayer";
import Footer from "../components/Footer";

export default function Home() {
  const [scrollY, setScrollY] = useState(0);

  // Fetch songs using TanStack Query with loading state
  const { data: songs = [], isLoading } = useQuery({
    queryKey: ["songs"],
    queryFn: fetchSongs,
  });

  // Scroll animation and parallax effects
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);

    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in-up");
          entry.target.classList.remove("opacity-30", "translate-y-8");
        } else {
          entry.target.classList.remove("animate-fade-in-up");
          entry.target.classList.add("opacity-30", "translate-y-8");
        }
      });
    }, observerOptions);

    // Observe all animated elements
    const animatedElements = document.querySelectorAll(".scroll-animate");
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <main className="bg-background-dark text-white font-sans overflow-x-hidden">
      <MusicPlayer />

      {/* Hero Section with Video Background */}
      <section className="relative h-screen w-full overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <div className="w-full h-full bg-black relative overflow-hidden">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{ transform: `translateY(${scrollY * 0.5}px)` }}
            >
              <source src="/videos/hero_background.mp4" type="video/mp4" />
              {/* Fallback image if video doesn't load */}
              <img
                src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1920&h=1080&fit=crop&crop=faces"
                alt="Hero background"
                className="w-full h-full object-cover"
              />
            </video>
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70"></div>
          </div>
        </div>

        <div className="relative z-10 w-full text-center px-4">
          <h1
            className="font-display text-6xl md:text-[12rem] lg:text-[14rem] font-bold text-white/50 inline-block leading-none tracking-wider transition-all duration-1000"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          >
            TheRealestt
          </h1>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 text-white/60">
          <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
          <div className="w-[1px] h-16 bg-gradient-to-b from-white/60 to-transparent"></div>
        </div>
      </section>

      {/* Artist Spotlight Section with Parallax */}
      <section className="relative py-32 overflow-hidden">
        {/* Parallax Background */}
        <div
          className="absolute inset-0 z-0"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <div className="w-full h-[125%] bg-cover bg-center bg-fixed relative">
            <img
              src="/images/paralax.png"
              alt="Artist spotlight background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Left Side - Artist Image */}
            <div className="scroll-animate transition-all duration-700 opacity-30 translate-y-8">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden border border-white/20">
                  <img
                    src="/images/img5.jpg"
                    alt="TheRealestt Artist Photo"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
                  />
                </div>
                {/* Decorative border */}
                <div className="absolute -top-4 -right-4 w-full h-full border border-primary/30 z-[-1]"></div>
              </div>
            </div>

            {/* Right Side - Text and Social Icons */}
            <div
              className="scroll-animate transition-all duration-700 opacity-30 translate-y-8"
              style={{ transitionDelay: "200ms" }}
            >
              <div className="space-y-8">
                {/* Label */}
                <p
                  className="text-[10px] uppercase tracking-[0.5em] text-primary font-bold"
                  style={{ textAlign: "right" }}
                >
                  Artist Spotlight
                </p>

                {/* Main Heading */}
                <h2
                  className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-tight"
                  style={{ textAlign: "right" }}
                >
                  <span className="text-white">Authentic</span>
                  <br />
                  <span className="text-primary italic">Artistry</span>
                </h2>

                {/* Social Media Icons */}
                <div className="pt-8">
                  <p
                    className="text-sm uppercase tracking-widest text-white/60 mb-6"
                    style={{ textAlign: "right" }}
                  >
                    Connect & Follow
                  </p>
                  <div className="flex items-center justify-end gap-6">
                    {/* Spotify */}
                    <a
                      target="_blank"
                      href="https://open.spotify.com/artist/6PKQKvMTizP7sUdDe9SfDI?si=UVhEUxZwR720GAK3SlL-ow"
                      className="group flex items-center justify-center w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-[#1DB954] hover:border-[#1DB954] transition-all duration-300"
                      title="Follow on Spotify"
                    >
                      <svg
                        className="w-5 h-5 text-white group-hover:scale-110 transition-transform"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
                      </svg>
                    </a>

                    {/* Facebook */}
                    <a
                      target="_blank"
                      href="https://www.facebook.com/share/1DYTtHrMgc/?mibextid=wwXIfr"
                      className="group flex items-center justify-center w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-[#1877F2] hover:border-[#1877F2] transition-all duration-300"
                      title="Follow on Facebook"
                    >
                      <svg
                        className="w-5 h-5 text-white group-hover:scale-110 transition-transform"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>

                    {/* Instagram */}
                    <a
                      target="_blank"
                      href="https://www.instagram.com/therealestt_77?igsh=MWRrOTdiMWpvY20zMg%3D%3D&utm_source=qr"
                      className="group flex items-center justify-center w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:border-transparent transition-all duration-300"
                      title="Follow on Instagram"
                    >
                      <svg
                        className="w-5 h-5 text-white group-hover:scale-110 transition-transform"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </a>

                    {/* TikTok */}
                    <a
                      target="_blank"
                      href="https://www.tiktok.com/@therealest_junny?_r=1&_t=ZS-94PVHrXkVK1"
                      className="group flex items-center justify-center w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-black hover:border-white transition-all duration-300"
                      title="Follow on TikTok"
                    >
                      <svg
                        className="w-5 h-5 text-white group-hover:scale-110 transition-transform"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-32 px-8 md:px-32 bg-black">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
          <div className="relative scroll-animate transition-all duration-700 opacity-30 translate-y-8">
            <img
              alt="Artist profile"
              className="w-full h-[650px] object-cover grayscale brightness-75 border border-white/10"
              src="/images/img6.jpg"
            />
            <div className="absolute -top-4 -left-4 w-full h-full border border-white/5 z-[-1]"></div>
          </div>

          <div
            className="pl-0 md:pl-10 scroll-animate transition-all duration-700 opacity-30 translate-y-8"
            style={{ transitionDelay: "200ms" }}
          >
            <span className="text-[10px] uppercase tracking-[0.5em] mb-8 block text-body-grey">
              Introduction
            </span>
            <h2 className="font-display text-6xl md:text-8xl mb-12 italic leading-none font-bold text-primary">
              Resonance <br />& Reality.
            </h2>

            <div className="space-y-8 text-base leading-relaxed font-light opacity-60 max-w-lg">
              <p>
                TheRealestt is an artist from South-South Nigeria, known for his
                creativity and distinctive sound. Blending Afroswing,
                Afro-Caribbean, and UK rap, he crafts music that expresses his
                life, style, and experiences.
              </p>
              <p>
                Every track is a reflection of his journey, bringing a unique
                voice to the global music scene. His innovative fusion of genres
                creates a sound that transcends borders while staying
                authentically rooted in his Nigerian heritage.
              </p>
            </div>

            <div className="mt-16">
              <Link
                to="/about"
                className="inline-flex items-center gap-6 text-[11px] uppercase tracking-[0.3em] font-black group border-b border-white/20 pb-2 hover:border-primary hover:text-primary transition-all"
              >
                Read Full Dossier
                <span className="material-symbols-outlined text-sm transition-transform group-hover:translate-x-3">
                  arrow_forward
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Discography Preview Section */}
      <section className="py-32 bg-black border-y border-white/5">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col md:flex-row justify-between items-baseline mb-20 gap-8 scroll-animate transition-all duration-700 opacity-30 translate-y-8">
            <div>
              <span className="text-[10px] uppercase tracking-[0.5em] mb-4 block text-body-grey">
                Sonic Archive
              </span>
              <h2 className="font-display text-5xl md:text-7xl font-bold italic text-primary">
                The Releases
              </h2>
            </div>
            <Link
              to="/discography"
              className="text-[10px] uppercase tracking-[0.3em] font-bold border border-white/20 px-8 py-3 hover:bg-primary hover:text-black transition-all"
            >
              Explore Collection
            </Link>
          </div>

          {/* Loading State for Songs */}
          {isLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-white/60 text-sm uppercase tracking-widest">
                  Loading Singles...
                </p>
              </div>
            </div>
          )}

          {/* Songs Grid */}
          {!isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12">
              {songs.slice(0, 3).map((single, i) => (
                <div
                  key={i}
                  className="group cursor-pointer scroll-animate transition-all duration-700 opacity-30 translate-y-8"
                  style={{ transitionDelay: `${i * 150}ms` }}
                >
                  <div className="relative overflow-hidden aspect-square mb-8">
                    <img
                      alt={single.title}
                      className="w-full h-full object-cover grayscale transition-all duration-1000 group-hover:scale-110 group-hover:grayscale-0"
                      src={single.image}
                    />
                    <a
                      target="_blank"
                      href={single.link}
                      className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px]"
                    >
                      <span className="px-10 py-4 border border-primary text-[10px] uppercase tracking-[0.3em] font-black text-primary bg-black/40">
                        Launch Stream
                      </span>
                    </a>
                  </div>

                  <h3 className="text-sm uppercase tracking-[0.2em] font-black mb-2">
                    {single.title}
                  </h3>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-body-grey font-bold">
                    • Single
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .animate-fade-in-up {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </main>
  );
}
